import {
    CreateOfferModal,
    DistributionDashboardHeader,
    DistributionDashboardTissues,
    DistributionLinks,
    DistributionMetrics,
    DraggableType,
    UnfilledSurgeries,
    getDraggableType,
} from "../..";
import { LoadingSpinner } from "../../../../component-library";
import { useStoredState } from "../../../../hooks";
import { useListQuery, useTissueQuery } from "../../../../queries";
import {
    DndContext,
    DragEndEvent,
    DragStartEvent,
    useSensor,
    useSensors,
    PointerSensor,
    DragOverlay,
} from "@dnd-kit/core";
import { useMemo, useState } from "react";
import { Surgery } from "../../../../models";
import { Lists } from "../../../lists";
import { createPatch, Operation } from "rfc6902";
import { useToasts } from "../../../../contexts";
import { SurgeryThumbnail } from "../../../surgeries";
import { getSurgeryTypeColorClass } from "../../../../helper-functions";

export function DistributionDashboard() {
    const { errorToast } = useToasts();
    const [showMobileMetrics, setShowMobileMetrics] = useStoredState("showDistroMobileMetrics", false);
    const [includePending, setIncludePending] = useStoredState("includeDistroPendingTissues", true);
    const [includeSclera, setIncludeSclera] = useStoredState("includeDistroSclera", false);
    const {
        listQuery: { data: listData, isLoading: isLoadingSurgeries },
    } = useListQuery({ listSlug: Lists.SurgeriesUnfilled });
    const surgeries = useMemo(() => (listData ?? []) as Surgery[], [listData]);
    const [showCreateOfferModal, setShowCreateOfferModal] = useState(false);
    const [selectedTissueId, setSelectedTissueId] = useState<number>(0);
    const [selectedSurgery, setSelectedSurgery] = useState<Surgery | undefined>();
    const [activeId, setActiveId] = useState<string | null>(null);
    const activeSurgery = useMemo(() => {
        if (!activeId?.startsWith("surgery-")) return null;
        const surgeryId = Number(activeId.split("-")[1]);
        return surgeries.find((s) => s.id === surgeryId);
    }, [activeId, surgeries]);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        })
    );

    const {
        distroDashboardQuery: { data, isLoading: isLoadingTissues },
        updateWithArgMutation,
    } = useTissueQuery({ queryType: "distroDashboard", includePending, includeSclera });
    const isLoading = isLoadingSurgeries || isLoadingTissues;
    const selectedTissue = useMemo(
        () => data?.tissues?.find((t) => t.tissue.id === selectedTissueId)?.tissue,
        [data, selectedTissueId]
    );

    const updateTissuePotentialSurgery = async (tissueId: number, potentialSurgeryId: number | undefined) => {
        const originalTissue = data?.tissues?.find((t) => t.tissue.id === tissueId)?.tissue;
        if (!originalTissue) return;
        const updatedTissue = { ...originalTissue, potentialSurgeryId };
        const operations: Operation[] = createPatch(originalTissue, updatedTissue);
        try {
            await updateWithArgMutation.mutateAsync({ tissueId, data: operations });
        } catch (error) {
            errorToast("Failed to update tissue potential surgery");
        }
    };

    const onDragEnd = async (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over) return;

        const sourceType = getDraggableType(active.id as string);
        const destinationType = getDraggableType(over.id as string);

        // surgery to placement: update tissue.potentialSurgeryId
        if (sourceType === DraggableType.Surgery && destinationType === DraggableType.PotentialPlacement) {
            const surgeryId = Number((active.id as string).split("-")[1]);
            const surgery = surgeries.find((s) => s.id === surgeryId);
            const tissueId = Number((over.id as string).split("-")[2]);
            await updateTissuePotentialSurgery(tissueId, surgery?.id);
        }

        // surgery to offer: create offer
        if (sourceType === DraggableType.Surgery && destinationType === DraggableType.Offer) {
            const surgeryId = Number((active.id as string).split("-")[1]);
            const surgery = surgeries.find((s) => s.id === surgeryId);
            setSelectedSurgery(surgery);
            const tissueId = Number((over.id as string).split("-")[2]);
            setSelectedTissueId(tissueId);
            setShowCreateOfferModal(true);
        }

        // placement to offer: create offer
        if (sourceType === DraggableType.PotentialPlacement && destinationType === DraggableType.Offer) {
            const sourceTissueId = Number((active.id as string).split("-")[2]);
            const sourceTissue = data?.tissues.find((t) => t.tissue.id === sourceTissueId)?.tissue;
            setSelectedSurgery(sourceTissue?.potentialSurgery);
            const destinationTissueId = Number((over.id as string).split("-")[2]);
            setSelectedTissueId(destinationTissueId);
            setShowCreateOfferModal(true);
        }

        // placement to placement: swap tissue.potentialSurgeryIds
        if (sourceType === DraggableType.PotentialPlacement && destinationType === DraggableType.PotentialPlacement) {
            // moving tissue 1 potential surgery to tissue 2 potential surgery
            const tissue1Id = Number((active.id as string).split("-")[2]);
            const tissue2Id = Number((over.id as string).split("-")[2]);
            const tissue1 = data?.tissues.find((t) => t.tissue.id === tissue1Id)?.tissue;
            const tissue2 = data?.tissues.find((t) => t.tissue.id === tissue2Id)?.tissue;
            const surgery1Id = tissue1?.potentialSurgeryId;
            const surgery2Id = tissue2?.potentialSurgeryId;

            if (!tissue1 || !surgery1Id) return;

            await Promise.all([
                updateTissuePotentialSurgery(tissue2Id, surgery1Id),
                updateTissuePotentialSurgery(tissue1Id, surgery2Id),
            ]);
        }

        // placement to surgery: update surgery.potentialTissueId to null
        if (sourceType === DraggableType.PotentialPlacement && destinationType === DraggableType.Surgery) {
            // handled by close button because of weird behavior
        }

        if (sourceType === DraggableType.Offer) {
            // cannot drag offer
        }
    };

    const onDragStart = (event: DragStartEvent) => {
        setActiveId(event.active.id as string);
    };

    if (isLoading) return <LoadingSpinner />;

    return (
        <div className="distro-dashboard">
            <DistributionDashboardHeader
                showMobileMetrics={showMobileMetrics}
                includePending={includePending}
                includeSclera={includeSclera}
                setShowMobileMetrics={setShowMobileMetrics}
                setIncludePending={setIncludePending}
                setIncludeSclera={setIncludeSclera}
            />

            <DistributionLinks />

            <DndContext sensors={sensors} onDragEnd={onDragEnd} onDragStart={onDragStart}>
                <DistributionMetrics showMobile={showMobileMetrics} data={data} />
                <DistributionDashboardTissues tissueSummaries={data?.tissues ?? []} />
                <UnfilledSurgeries surgeries={surgeries} />
                <DragOverlay>
                    {activeSurgery ? (
                        <div
                            className={`surgery-list-item draggable-surgery ${getSurgeryTypeColorClass(activeSurgery)}`}
                        >
                            <SurgeryThumbnail surgery={activeSurgery} surgeon={activeSurgery.surgeon} />
                        </div>
                    ) : null}
                </DragOverlay>
            </DndContext>

            <CreateOfferModal
                show={showCreateOfferModal}
                onHide={() => setShowCreateOfferModal(false)}
                tissue={selectedTissue}
                surgery={selectedSurgery}
            />
        </div>
    );
}
